import { AuthIdentity } from "@/store/modules/auth";

const ACCOUNTS_KEY = "edms.remembered-accounts.v2";
const LEGACY_ACCOUNTS_KEY = "edms.remembered-accounts.v1";
const LOGO_DATABASE = "edms-account-assets-v2";
const LEGACY_LOGO_DATABASE = "edms-account-assets";
const LOGO_STORE = "logos";
const MAX_ACCOUNTS = 20;
const PUBLIC_ID_PATTERN = /^[0-9A-Za-z]{16}$/;

let legacyStorageCleared = false;

export interface RememberedAccount {
  key: string;
  userId: string;
  login: string;
  tenantId: string | null;
  tenantName: string;
  logoFileGuid: string | null;
  platformAdmin: boolean;
  lastUsedAt: number;
}

export interface RememberedAccountView extends RememberedAccount {
  logoDataUrl: string | null;
}

export function identityKey(identity: AuthIdentity): string {
  return `${identity.id}:${identity.tenant?.id ?? "platform"}`;
}

export function listRememberedAccounts(): RememberedAccount[] {
  clearLegacyAccountStorage();
  try {
    const stored = localStorage.getItem(ACCOUNTS_KEY);
    const accounts = stored ? JSON.parse(stored) : [];
    if (!Array.isArray(accounts)) {
      return [];
    }
    return accounts
      .filter(isRememberedAccount)
      .sort((left, right) => right.lastUsedAt - left.lastUsedAt)
      .slice(0, MAX_ACCOUNTS);
  } catch {
    try {
      localStorage.removeItem(ACCOUNTS_KEY);
    } catch {
      // Remembering an account is optional and must never block sign-in.
    }
    return [];
  }
}

export async function listRememberedAccountViews(): Promise<
  RememberedAccountView[]
> {
  const accounts = listRememberedAccounts();
  return Promise.all(
    accounts.map(async (account) => ({
      ...account,
      logoDataUrl: await loadLogo(account.key),
    }))
  );
}

export async function rememberAccount(
  identity: AuthIdentity,
  logoDataUrl?: string | null
): Promise<RememberedAccount> {
  const key = identityKey(identity);
  const account: RememberedAccount = {
    key,
    userId: identity.id,
    login: identity.login || identity.username || "",
    tenantId: identity.tenant?.id ?? null,
    tenantName: identity.tenant?.name || "",
    logoFileGuid: identity.tenant?.logoFileGuid || null,
    platformAdmin: identity.platformAdmin === true,
    lastUsedAt: Date.now(),
  };
  const previousAccounts = listRememberedAccounts();
  const nextAccounts = [
    account,
    ...previousAccounts
      .filter((item) => item.key !== key)
      .map((item) =>
        account.tenantId !== null && item.tenantId === account.tenantId
          ? {
              ...item,
              tenantName: account.tenantName,
              logoFileGuid: account.logoFileGuid,
            }
          : item
      ),
  ].slice(0, MAX_ACCOUNTS);
  if (!writeAccounts(nextAccounts)) {
    return account;
  }

  await synchronizeLogoCache(
    nextAccounts,
    account.tenantId,
    account.logoFileGuid,
    logoDataUrl,
    key
  );

  const retainedKeys = new Set(nextAccounts.map((item) => item.key));
  await deleteLogos(
    previousAccounts
      .filter((item) => !retainedKeys.has(item.key))
      .map((item) => item.key)
  );
  return account;
}

/** Updates branding only for saved accounts of the explicitly selected tenant. */
export async function synchronizeRememberedTenantBranding(
  tenantId: string,
  tenantName: string,
  logoFileGuid: string | null,
  logoDataUrl?: string | null
): Promise<void> {
  if (!isPublicId(tenantId)) {
    return;
  }
  const accounts = listRememberedAccounts();
  const matchingAccounts = accounts.filter(
    (account) => account.tenantId === tenantId
  );
  if (!matchingAccounts.length) {
    return;
  }
  const nextAccounts = accounts.map((account) =>
    account.tenantId === tenantId
      ? { ...account, tenantName, logoFileGuid }
      : account
  );
  if (!writeAccounts(nextAccounts)) {
    return;
  }
  await synchronizeLogoCache(
    nextAccounts,
    tenantId,
    logoFileGuid,
    logoDataUrl
  );
}

export async function removeRememberedAccount(key: string): Promise<void> {
  const nextAccounts = listRememberedAccounts().filter(
    (account) => account.key !== key
  );
  writeAccounts(nextAccounts);
  await deleteLogo(key);
}

export async function getRememberedAccount(
  key: string
): Promise<RememberedAccount | undefined> {
  return listRememberedAccounts().find((account) => account.key === key);
}

async function loadLogo(key: string): Promise<string | null> {
  return withLogoStore<string | null>("readonly", (store, resolve) => {
    const request = store.get(key);
    request.onsuccess = () =>
      resolve(
        typeof request.result === "string" &&
          isSupportedLogoDataUrl(request.result)
          ? request.result
          : null
      );
  }, null);
}

async function deleteLogo(key: string): Promise<void> {
  await deleteLogos([key]);
}

async function saveLogos(keys: string[], dataUrl: string): Promise<void> {
  if (!keys.length) {
    return;
  }
  await withLogoStore<void>("readwrite", (store, resolve) => {
    let pending = keys.length;
    keys.forEach((key) => {
      const request = store.put(dataUrl, key);
      request.onsuccess = () => {
        pending -= 1;
        if (pending === 0) {
          resolve();
        }
      };
    });
  });
}

async function deleteLogos(keys: string[]): Promise<void> {
  if (!keys.length) {
    return;
  }
  await withLogoStore<void>("readwrite", (store, resolve) => {
    let pending = keys.length;
    keys.forEach((key) => {
      const request = store.delete(key);
      request.onsuccess = () => {
        pending -= 1;
        if (pending === 0) {
          resolve();
        }
      };
    });
  });
}

async function synchronizeLogoCache(
  accounts: RememberedAccount[],
  tenantId: string | null,
  logoFileGuid: string | null,
  logoDataUrl?: string | null,
  preferredKey?: string
): Promise<void> {
  const matchingAccounts = accounts.filter((account) =>
    tenantId === null
      ? account.key === preferredKey
      : account.tenantId === tenantId
  );
  if (!matchingAccounts.length) {
    return;
  }

  let resolvedLogo = logoDataUrl;
  if (resolvedLogo === undefined && logoFileGuid) {
    const orderedAccounts = preferredKey
      ? [
          ...matchingAccounts.filter((account) => account.key === preferredKey),
          ...matchingAccounts.filter((account) => account.key !== preferredKey),
        ]
      : matchingAccounts;
    for (const account of orderedAccounts) {
      const cachedLogo = await loadLogo(account.key);
      if (cachedLogo) {
        resolvedLogo = cachedLogo;
        break;
      }
    }
  }

  if (resolvedLogo) {
    const safeLogo = isSupportedLogoDataUrl(resolvedLogo)
      ? resolvedLogo
      : null;
    if (!safeLogo) {
      await deleteLogos(matchingAccounts.map((account) => account.key));
      return;
    }
    await saveLogos(
      matchingAccounts.map((account) => account.key),
      safeLogo
    );
  } else if (resolvedLogo === null || !logoFileGuid) {
    await deleteLogos(matchingAccounts.map((account) => account.key));
  }
}

async function withLogoStore<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore, resolve: (value: T) => void) => void,
  fallback?: T
): Promise<T> {
  if (typeof indexedDB === "undefined") {
    return fallback as T;
  }
  try {
    const database = await openLogoDatabase();
    return await new Promise<T>((resolve, reject) => {
      const transaction = database.transaction(LOGO_STORE, mode);
      transaction.oncomplete = () => database.close();
      transaction.onerror = () => {
        database.close();
        reject(transaction.error);
      };
      transaction.onabort = () => {
        database.close();
        reject(transaction.error);
      };
      try {
        action(transaction.objectStore(LOGO_STORE), resolve);
      } catch (error) {
        database.close();
        reject(error);
      }
    });
  } catch {
    return fallback as T;
  }
}

function isSupportedLogoDataUrl(value: string): boolean {
  return /^data:image\/(?:png|jpeg|webp);base64,/i.test(value);
}

function isPublicId(value: unknown): value is string {
  return typeof value === "string" && PUBLIC_ID_PATTERN.test(value);
}

function clearLegacyAccountStorage(): void {
  if (legacyStorageCleared) {
    return;
  }
  legacyStorageCleared = true;
  let hadLegacyAccounts = false;
  try {
    hadLegacyAccounts = localStorage.getItem(LEGACY_ACCOUNTS_KEY) !== null;
    localStorage.removeItem(LEGACY_ACCOUNTS_KEY);
  } catch {
    // Remembering an account is optional and must never block sign-in.
  }
  if (hadLegacyAccounts && typeof indexedDB !== "undefined") {
    try {
      indexedDB.deleteDatabase(LEGACY_LOGO_DATABASE);
    } catch {
      // Cached branding is optional and must never block sign-in.
    }
  }
}

function openLogoDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(LOGO_DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(LOGO_STORE)) {
        request.result.createObjectStore(LOGO_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function isRememberedAccount(value: unknown): value is RememberedAccount {
  const account = value as Partial<RememberedAccount> | null;
  return Boolean(
    account &&
      typeof account.key === "string" &&
      isPublicId(account.userId) &&
      typeof account.login === "string" &&
      (isPublicId(account.tenantId) || account.tenantId === null) &&
      account.key ===
        `${account.userId}:${account.tenantId ?? "platform"}` &&
      typeof account.tenantName === "string" &&
      (typeof account.logoFileGuid === "string" ||
        account.logoFileGuid === null) &&
      typeof account.platformAdmin === "boolean" &&
      typeof account.lastUsedAt === "number"
  );
}

function writeAccounts(accounts: RememberedAccount[]): boolean {
  try {
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
    return true;
  } catch {
    return false;
  }
}
